import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { InjectionToken, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';

import {
  AttachmentBlobService,
  AttachmentEnvToken,
} from '@plugins/client/attachment/attachment-blob.service';
import { ImageResizeService } from '@plugins/client/attachment/image-resize.service';
import { AuthModule, authInterceptorFn } from '@plugins/client/auth';
import { DECORATIVE_ELEMENT_MIN_CONTRAST } from '@plugins/client/colors/constants';
import { ContrastColorValues } from '@plugins/client/colors/types';
import { languageHeadersInterceptorFn } from '@plugins/client/interceptors/language-headers.interceptor';
import {
  JsonDateModule,
  jsonDateInterceptorFn,
} from '@plugins/client/json-date';
import { MultilanguageService } from '@plugins/client/multilanguage/multilanguage.service';
import { EntitledAuthorPipe, IsAuthorPipe } from '@plugins/client/post/pipes';
import { StartupSpinnerComponent } from '@plugins/client/spinners';
import { providePluginsPrimeNG } from '@plugins/client/styles/primeng';
import { provideAppVersion } from '@plugins/client/tokens/app-version.token';
import { localeIdProvider } from '@plugins/client/translate/providers';
import {
  TRANSLATE_CONFIG,
  TranslateConfigToken,
} from '@plugins/client/translate/services';
import { MiddlewareSdkModule } from '@plugins/middleware-sdk';
import { UserDetailsService } from '@plugins/userdetails-client';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { AnswerState } from './shared/store/answer/answer.state';
import { CommentState } from './shared/store/comment/comment.state';
import { InstanceState } from './shared/store/instance/instance.state';
import { QuestionState } from './shared/store/question/question.state';

export const DECORATIVE_CONTRAST_TOKEN = new InjectionToken<
  Pick<ContrastColorValues, 'contrastRatio'>
>('faq-decorative-contrast-token');

const sharedServices = [
  ImageResizeService,
  AttachmentBlobService,
  { provide: AttachmentEnvToken, useValue: environment },
];

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    MiddlewareSdkModule,
    AuthModule.forRoot(),
    JsonDateModule.configure({ paths: ['/api/'] }),
    StartupSpinnerComponent,
    NgxsModule.forRoot(
      [InstanceState, QuestionState, AnswerState, CommentState],
      {
        developmentMode: !environment.production,
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
    ),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    SharedModule,
  ],
  declarations: [AppComponent],
  providers: [
    ...sharedServices,
    provideHttpClient(
      withInterceptors([
        authInterceptorFn,
        languageHeadersInterceptorFn,
        jsonDateInterceptorFn,
      ]),
    ),
    provideAnimationsAsync(),
    providePluginsPrimeNG(),
    MultilanguageService,
    IsAuthorPipe,
    EntitledAuthorPipe,
    UserDetailsService,
    localeIdProvider(environment.languages),
    provideAppVersion(),
    {
      provide: DECORATIVE_CONTRAST_TOKEN,
      useValue: { contrastRatio: DECORATIVE_ELEMENT_MIN_CONTRAST },
    },
    {
      provide: TRANSLATE_CONFIG,
      useValue: {
        languages: environment.languages,
        fallbackLanguage: environment.languages[0],
        useFallbackLanguageOnMissing: true,
      } satisfies TranslateConfigToken,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
